import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface SelectDropdownOption {
  /** Unique value for this option */
  value: string;
  /** Primary label shown bold */
  label: string;
  /** Optional secondary label shown smaller below */
  subLabel?: string;
  /** Optional icon/flag/avatar — any ReactNode */
  icon?: React.ReactNode;
}

interface SelectDropdownProps {
  /** Dropdown header title (ALL-CAPS label inside panel) */
  heading?: string;
  /** All available options */
  options: SelectDropdownOption[];
  /** Currently selected value */
  value: string;
  /** Called when the user selects an option */
  onChange: (value: string) => void;
  /** Placeholder text when nothing is selected */
  placeholder?: string;
  /** Extra classes for the trigger button wrapper */
  className?: string;
  /** Align the panel: 'left' | 'right' (default: 'left') */
  align?: 'left' | 'right';
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  heading = 'SELECT',
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  align = 'left',
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-semibold transition-all cursor-pointer select-none
          ${open || selected
            ? 'border-slate-300 bg-white shadow-md text-slate-800'
            : 'border-slate-200 bg-white shadow-sm text-slate-700 hover:shadow-md hover:border-slate-300'
          }`}
      >
        {selected?.icon && (
          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
            {selected.icon}
          </span>
        )}
        <span className="max-w-[140px] truncate">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className={`absolute top-full mt-2 z-50 min-w-[220px] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden
            ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          {/* Header */}
          <div className="px-4 pt-3.5 pb-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
              {heading}
            </span>
          </div>

          {/* Options */}
          <ul className="pb-2 max-h-72 overflow-y-auto">
            {options.map((opt) => {
              const isActive = opt.value === value;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer text-left
                      ${isActive ? 'bg-emerald-50/70' : 'hover:bg-slate-50'}`}
                  >
                    {opt.icon !== undefined && (
                      <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center">
                        {opt.icon}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block text-sm font-semibold truncate leading-tight
                          ${isActive ? 'text-slate-900' : 'text-slate-700'}`}
                      >
                        {opt.label}
                      </span>
                      {opt.subLabel && (
                        <span className="block text-xs text-slate-400 mt-0.5 truncate leading-tight">
                          {opt.subLabel}
                        </span>
                      )}
                    </span>
                    {isActive && (
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
